import { useEffect, useState } from 'react';

import { gql } from '@apollo/client';

import { OdProtosSellCommon_Product, OdProtosWebData_Offer_State } from '__generated__/globalTypes';

import { useQuery } from 'platform/apollo';
import { useObservability } from 'platform/observability';

import { isProductQualified } from 'shared/helpers/QualificationContext';

import { SellerLeadFragment } from 'sellDirect/graphql/fragments/__generated__/SellerLeadFragment';

import {
  GetExclusiveOffersQualificationQuery,
  GetExclusiveOffersQualificationQueryVariables,
} from './__generated__/GetExclusiveOffersQualificationQuery';

/** Hook to check if seller should see the EO experience */
export function useIsExclusiveOffersQualified({
  sellerLead,
}: {
  /**
   * Seller lead data from the SellDirectLeadContext
   * This lead comes from seller.getLead, which is lead data from the leads table in seller, versus
   * sellDirect.getLead, which is lead data from the leads table in web.
   */
  sellerLead?: SellerLeadFragment | null | undefined;
}) {
  const { trackError } = useObservability();

  const qualifiedForSellWithExclusives = isProductQualified(
    OdProtosSellCommon_Product.SELL_WITH_EXCLUSIVES,
    sellerLead,
  );
  const qualifiedForSellDirect = isProductQualified(
    OdProtosSellCommon_Product.OPENDOOR_DIRECT,
    sellerLead,
  );

  // If eligible thus far, fetch the sellDirect.lead object because we need
  // to check the state of the web lead's offer
  const sellDirectLeadQuery = useQuery<
    GetExclusiveOffersQualificationQuery,
    GetExclusiveOffersQualificationQueryVariables
  >(GET_EXCLUSIVE_OFFERS_QUALIFICATION, {
    variables: { sellerLeadUUID: sellerLead?.id ?? '' },
    skip: !qualifiedForSellWithExclusives,
    onError: (error) => {
      trackError('GET_SELL_DIRECT', error);
    },
    context: { linkContext: { team: 'marketplace' } },
  });

  // Use a custom loading state instead of leadQuery.loading because we will
  // refetch the query if the lead's offer is null, which happens if the
  // backend is still creating the offer
  const [loading, setLoading] = useState(sellDirectLeadQuery.loading);
  const onLoadingChange = async () => {
    if (sellDirectLeadQuery.loading) {
      setLoading(true);
      return;
    }

    // Refetch sellDirectLead if the lead is qualified for sell direct but the offer relation is empty
    // this can happen if the web backend is still creating the offer
    if (
      qualifiedForSellWithExclusives &&
      qualifiedForSellDirect &&
      !sellDirectLeadQuery.data?.sellDirect?.getLead?.lead?.latestOffer &&
      !sellDirectLeadQuery.error
    ) {
      let lead = undefined;
      const start = Date.now();
      while (
        qualifiedForSellDirect &&
        (lead?.isQualified ?? true) &&
        !lead?.latestOffer &&
        Date.now() - start < 30000
      ) {
        await sleep(2000);
        const next = await sellDirectLeadQuery.refetch();
        lead = next?.data?.sellDirect?.getLead?.lead;
      }
    }

    setLoading(false);
  };
  useEffect(() => {
    onLoadingChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
  }, [sellDirectLeadQuery.loading]);

  const sellDirectLead = sellDirectLeadQuery.data?.sellDirect?.getLead?.lead;
  const isExclusiveOffersQualified: boolean =
    qualifiedForSellWithExclusives &&
    (!qualifiedForSellDirect ||
      hasValidSellDirectOfferStateForExclusives(sellDirectLead?.latestOffer?.state));

  return { loading, isExclusiveOffersQualified };
}

/**  */
function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Checks if offer is in a state that is qualified for exclusives
 * Once the offer is further along (accepted and further), we want to show the sell direct experience.
 */
export function hasValidSellDirectOfferStateForExclusives(offerState?: string) {
  return [
    OdProtosWebData_Offer_State.EXPIRED,
    OdProtosWebData_Offer_State.SENT,
    OdProtosWebData_Offer_State.FINAL_OFFER_UNRELEASED,
  ].includes(offerState as OdProtosWebData_Offer_State);
}

export const GET_EXCLUSIVE_OFFERS_QUALIFICATION = gql`
  query GetExclusiveOffersQualificationQuery($sellerLeadUUID: String!) {
    sellDirect {
      getLead(input: { lead: { sellerLeadUuid: $sellerLeadUUID } }) {
        lead {
          isQualified
          latestOffer {
            state
          }
        }
      }
    }
  }
`;
